import React from 'react'
import PropTypes from 'prop-types'
import { makeStyles, alpha } from '@material-ui/core/styles'
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Grid,
  SvgIcon,
  MenuItem,
  Menu,
  Grow,
  Tooltip,
  CircularProgress,
  Fab,
} from '@material-ui/core'
import {
  Menu as MenuIcon,
  MenuOpen,
  ArrowForwardIos,
  ArrowBackIos,
  AddBox,
  InfoOutlined,
  OpenInNew,
} from '@material-ui/icons'
import clsx from 'clsx'
import { ReactComponent as TipsAndUpdates } from 'assets/svg/tips_and_updates.svg'
import { ReactComponent as PreviousAnswer } from 'assets/svg/previous_answer.svg'
import { ReactComponent as NextAnswer } from 'assets/svg/next_answer.svg'
import { ReactComponent as AiIcon } from 'assets/svg/ai_icon.svg'
import { useReaderContext } from './context/reader-context'
import { useMemo, useState } from 'react'

import AssignmentDialog from './create-assignment-dialog'
import { useSelector } from 'react-redux'
import { userRoleSelector } from 'store/auth/selectors'
import roles from 'constants/roles'
import { useLaunchPlatforms } from 'api/Package/Packages/queries'
import {
  useLaunchLudenso,
  useLudensoRemainings,
} from 'api/SchoolFeatures/Ludenso/queries'
import useNotification from 'hooks/useNotification'
import parseError from 'helpers/errorHelpers'

const drawerWidth = 300

const useStyles = makeStyles((theme) => ({
  appBar: {
    backgroundColor: theme.palette.background.paper,
    color: 'black',
    zIndex: theme.zIndex.drawer + 1,
    overflowX: 'hidden',
    maxWidth: '100%',
    right: 0,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  toolbar: {
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    overflow: 'hidden',
    maxWidth: '100%',
  },
  menuButton: {},
  arrowButton: {
    margin: theme.spacing(0, 1),
  },
  hide: {
    display: 'none',
  },
  title: {},
  header: {
    fontSize: 30,
    fontWeight: 300,
  },
  iconWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  text: {
    color: '#3d5265',
  },
  cosmosAvatar: {
    width: theme.spacing(4),
    height: theme.spacing(4),
    marginRight: theme.spacing(1),
    cursor: 'pointer',
  },
  floatingCosmosButton: {
    position: 'fixed',
    bottom: theme.spacing(3),
    right: theme.spacing(3),
    zIndex: theme.zIndex.speedDial || 1050,
    backgroundColor: 'transparent',
    color: theme.palette.primary.main,
    boxShadow: 'none',
    padding: 0,
    transition: 'transform 0.2s ease',
    '&:hover': {
      backgroundColor: 'transparent',
      transform: 'scale(1.1)',
    },
  },
  cosmosMenuPaper: {
    marginTop: '-16px !important',
    borderRadius: '16px !important',
    minWidth: 260,
    overflow: 'hidden',
    boxShadow:
      '0 12px 30px -4px rgba(0, 0, 0, 0.15), 0 4px 12px -2px rgba(0, 0, 0, 0.08) !important',
    border: '1px solid rgba(0, 0, 0, 0.08)',
  },
  cosmosMenuHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 18px',
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid rgba(0, 0, 0, 0.07)',
    cursor: 'default',
    outline: 'none',
  },
  cosmosMenuItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 18px',
    fontSize: 14,
    fontWeight: 600,
    color: theme.palette.text.primary,
    transition: 'background-color 0.2s ease, color 0.2s ease',
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
      color: theme.palette.primary.main,
    },
  },
  tokenBadge: {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    color: theme.palette.primary.main,
    padding: '3px 9px',
    borderRadius: 12,
    fontWeight: 700,
    fontSize: 12,
    marginLeft: 8,
  },
}))

function ReaderAppbar({
  hasNext,
  hasPrev,
  onNext,
  onPrev,
  onShowAnswers,
  currentActivity,
  onShowNextAnswer,
  onHideLastAnswer,
  enableShowAnswers,
  enableCreateAssignment,
}) {
  const classes = useStyles()

  const [moreAnchorEl, setMoreAnchorEl] = React.useState(null)
  const isMenuOpen = Boolean(moreAnchorEl)
  const [cosmosAnchorEl, setCosmosAnchorEl] = React.useState(null)
  const isCosmosMenuOpen = Boolean(cosmosAnchorEl)
  const [openCreateAssignmentMenu, setOpenCreateAssignmentMenu] =
    React.useState(false)
  const [isLaunchingLudenso, setIsLaunchingLudenso] = useState(false)

  const { addNotification } = useNotification()
  const launchPlatforms = useLaunchPlatforms()
  const launchLudenso = useLaunchLudenso()

  const userRole = useSelector(userRoleSelector)
  const IS_STUDENT = userRole === roles.STUDENT //|| userRole === roles.MEMBER
  const username = useSelector((state) => state.auth.user?.username)
  const fullName = useSelector((state) => state.auth.user?.full_name)
  const currentSubSchool = useSelector((state) => state.auth.currentSubSchool)
  const SCHOOL_TYPE_2 = currentSubSchool?.type_id === 2
  const SCHOOL_TYPE = currentSubSchool?.type_id
  const currentSchoolID = useMemo(() => {
    return SCHOOL_TYPE_2 ? currentSubSchool?.user_id : currentSubSchool?.id
  }, [SCHOOL_TYPE_2, currentSubSchool?.user_id, currentSubSchool?.id])

  const {
    info: {
      title,
      isTabPages,
      availableActivities,
      pkg,
      packageId,
      ludensoPackages = [],
    },
    readerMode: { IS_EPUB, IS_DIGIPUB, IS_DIWB },
    drawer: { open, handleToggleDrawer },
  } = useReaderContext()

  const isAllowedCosmosRole = useMemo(() => {
    return [
      roles.TEACHER,
      roles.INDTEACHER,
      roles.MASTER,
      roles.SCHOOLMANAGER,
    ].includes(userRole)
  }, [userRole])

  const isLudensoPackage = useMemo(() => {
    return (
      typeof pkg?.data?.LudensoPackage === 'object' &&
      pkg?.data?.LudensoPackage !== null &&
      Object.keys(pkg?.data?.LudensoPackage).length > 0
    )
  }, [pkg?.data?.LudensoPackage])

  const shouldFetchRemainings = isLudensoPackage && isAllowedCosmosRole

  const ludensoRemainings = useLudensoRemainings({
    queryOptions: {
      enabled: Boolean(shouldFetchRemainings),
      staleTime: Infinity,
      cacheTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    },
  })

  const isLudensoActive = Boolean(ludensoRemainings.data?.active)

  const showCosmosIcon = useMemo(() => {
    return Boolean(shouldFetchRemainings && isLudensoActive)
  }, [shouldFetchRemainings, isLudensoActive])

  const needsRefetchRemainingsRef = React.useRef(false)

  const handleOpenLudenso = async () => {
    if (isLaunchingLudenso) return
    setIsLaunchingLudenso(true)
    try {
      const currentLudensoId = pkg?.data?.LudensoPackage?.ludenso_id ?? null
      // const dataLaunch = await launchPlatforms.mutateAsync(currentPkgId)

      const ludensoIds = (ludensoPackages || [])
        .map(
          (item) =>
            item?.Package?.LudensoPackage?.ludenso_id ??
            item?.LudensoPackage?.ludenso_id
        )
        .filter((id) => id !== null && id !== undefined && id !== '')

      if (
        currentLudensoId !== null &&
        currentLudensoId !== undefined &&
        !ludensoIds.includes(currentLudensoId)
      ) {
        ludensoIds.push(currentLudensoId)
      }

      const payload = {
        packages: Array.from(new Set(ludensoIds)),
        currentPackage: currentLudensoId,
      }

      const ludensoRes = await launchLudenso.mutateAsync(payload)

      if (typeof ludensoRes === 'string') {
        addNotification({
          type: 'error',
          message: parseError(ludensoRes),
        })
      } else {
        const baseUrl = ludensoRes?.url || ludensoRes?.data?.url
        if (baseUrl) {
          window.open(baseUrl, '_blank')
          needsRefetchRemainingsRef.current = true
        } else {
          addNotification({
            type: 'error',
            message: 'No launch URL received for Ludenso',
          })
        }
      }
    } catch (error) {
      addNotification({
        type: 'error',
        message: parseError(error, 'Error launching Ludenso'),
      })
    } finally {
      setIsLaunchingLudenso(false)
    }
  }

  const hasDrawer = useMemo(
    () => availableActivities.length > 0,
    [availableActivities]
  )

  const hideControls = useMemo(
    () => (IS_EPUB || IS_DIWB ? false : IS_DIGIPUB ? false : true),
    [IS_EPUB, IS_DIWB, IS_DIGIPUB]
  )

  const handleMenuOpen = (event) => {
    setMoreAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setMoreAnchorEl(null)
  }

  const handleCosmosMenuOpen = (event) => {
    setCosmosAnchorEl(event.currentTarget)
    if (needsRefetchRemainingsRef.current) {
      needsRefetchRemainingsRef.current = false
      ludensoRemainings.refetch()
    }
  }

  const handleCosmosMenuClose = () => {
    setCosmosAnchorEl(null)
  }

  const menuId = 'primary-search-account-menu'
  const cosmosMenuId = 'cosmos-account-menu'

  const renderMenu = (
    <Menu
      anchorEl={moreAnchorEl}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      id={menuId}
      keepMounted
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem onClick={onShowAnswers}>
        <IconButton aria-controls={menuId} className={classes.iconWrapper}>
          <SvgIcon component={TipsAndUpdates} fontSize="inherit" />
        </IconButton>
        <div>All answers</div>
      </MenuItem>
      <MenuItem onClick={onShowNextAnswer}>
        <IconButton aria-controls={menuId} className={classes.iconWrapper}>
          <SvgIcon component={NextAnswer} fontSize="inherit" />
        </IconButton>
        <div className="classes.text">Next answer</div>
      </MenuItem>
      <MenuItem onClick={onHideLastAnswer}>
        <IconButton aria-controls={menuId} className={classes.iconWrapper}>
          <SvgIcon component={PreviousAnswer} fontSize="inherit" />
        </IconButton>
        <div>Previous answers</div>
      </MenuItem>
    </Menu>
  )

  const remainingCount = useMemo(() => {
    const raw =
      ludensoRemainings.data?.remaining ??
      ludensoRemainings.data?.remainings ??
      (typeof ludensoRemainings.data === 'number' ? ludensoRemainings.data : 0)
    return Number(raw) || 0
  }, [ludensoRemainings.data])

  const renderCosmosMenu = (
    <Menu
      anchorEl={cosmosAnchorEl}
      getContentAnchorEl={null}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      id={cosmosMenuId}
      keepMounted
      transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      open={isCosmosMenuOpen}
      onClose={handleCosmosMenuClose}
      classes={{ paper: classes.cosmosMenuPaper }}
    >
      <div className={classes.cosmosMenuHeader}>
        {ludensoRemainings.isLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <CircularProgress size={16} color="primary" />
            <Typography variant="body2">Loading...</Typography>
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ fontWeight: 500 }}
              >
                Remaining Tokens:
              </Typography>
              <span className={classes.tokenBadge}>{remainingCount}</span>
            </div>
            <InfoOutlined
              color="action"
              style={{ fontSize: 18, marginLeft: 8, opacity: 0.7 }}
            />
          </div>
        )}
      </div>
      <MenuItem
        onClick={() => {
          handleCosmosMenuClose()
          handleOpenLudenso()
        }}
        disabled={
          isLaunchingLudenso ||
          ludensoRemainings.isLoading ||
          remainingCount <= 0
        }
        className={classes.cosmosMenuItem}
      >
        <span>Launch AI Assistant</span>
        <OpenInNew
          color="primary"
          style={{ fontSize: 18, marginLeft: 12, opacity: 0.8 }}
        />
      </MenuItem>
    </Menu>
  )

  const openCreateAssignmentDialog = () => {
    setOpenCreateAssignmentMenu(true)
  }

  return (
    <>
      <AppBar
        position="fixed"
        className={clsx(classes.appBar, {
          [classes.appBarShift]: open && hasDrawer,
        })}
      >
        <Toolbar disableGutters={false} className={classes.toolbar}>
          {hasDrawer ? (
            <IconButton
              onClick={handleToggleDrawer}
              className={classes.menuButton}
              color="primary"
            >
              {open ? <MenuOpen /> : <MenuIcon />}
            </IconButton>
          ) : null}
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            spacing={5}
          >
            {hideControls || isTabPages ? null : (
              <Grid item>
                <IconButton
                  disabled={!hasPrev}
                  onClick={onPrev}
                  color="primary"
                >
                  <ArrowBackIos />
                </IconButton>
              </Grid>
            )}

            <Grid item>
              <Typography
                component="div"
                variant="h6"
                className={classes.title}
              >
                {title}
              </Typography>
            </Grid>
            {hideControls || isTabPages ? null : (
              <Grid item>
                <IconButton
                  disabled={!hasNext}
                  onClick={onNext}
                  color="primary"
                >
                  <ArrowForwardIos />
                </IconButton>
              </Grid>
            )}
          </Grid>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {IS_STUDENT ? (
              <Grow in={enableShowAnswers}>
                <IconButton
                  onClick={onShowAnswers}
                  aria-controls={menuId}
                  className={classes.iconWrapper}
                  color="primary"
                >
                  <SvgIcon
                    color="primary"
                    component={TipsAndUpdates}
                    fontSize="inherit"
                  />
                </IconButton>
              </Grow>
            ) : IS_DIWB ? (
              <Grow in={enableShowAnswers}>
                <IconButton
                  aria-controls={menuId}
                  onClick={handleMenuOpen}
                  className={classes.iconWrapper}
                  color="primary"
                >
                  <SvgIcon
                    color="primary"
                    component={TipsAndUpdates}
                    fontSize="inherit"
                  />
                </IconButton>
              </Grow>
            ) : (
              <>
                <Grow in={enableShowAnswers}>
                  <IconButton
                    aria-controls={menuId}
                    onClick={onShowAnswers}
                    className={classes.iconWrapper}
                    color="primary"
                  >
                    <SvgIcon
                      color="primary"
                      component={TipsAndUpdates}
                      fontSize="inherit"
                    />
                  </IconButton>
                </Grow>
                <Grow in={enableCreateAssignment}>
                  <Tooltip title="Create Assignment" arrow>
                    <IconButton
                      aria-controls={menuId}
                      onClick={openCreateAssignmentDialog}
                      className={classes.iconWrapper}
                      color="primary"
                    >
                      <SvgIcon
                        color="primary"
                        component={AddBox}
                        fontSize="inherit"
                      />
                    </IconButton>
                  </Tooltip>
                </Grow>
              </>
            )}
          </div>
          {renderMenu}
          {renderCosmosMenu}
        </Toolbar>
      </AppBar>
      {showCosmosIcon && (
        <IconButton
          onClick={handleCosmosMenuOpen}
          disabled={isLaunchingLudenso}
          aria-controls={cosmosMenuId}
          className={classes.floatingCosmosButton}
          aria-label="AI Assistant"
        >
          {isLaunchingLudenso ? (
            <CircularProgress size={28} color="inherit" />
          ) : (
            <SvgIcon
              color="inherit"
              component={AiIcon}
              viewBox="0 0 24 24"
              style={{ width: 44, height: 44, fontSize: 44 }}
            />
          )}
        </IconButton>
      )}
      <AssignmentDialog
        open={openCreateAssignmentMenu}
        currentActivity={currentActivity}
        onClose={() => setOpenCreateAssignmentMenu(false)}
      />
    </>
  )
}

ReaderAppbar.propTypes = {
  hasDrawer: PropTypes.bool,
  hideControls: PropTypes.bool,
  currentActivity: PropTypes.any,
  open: PropTypes.bool,
  onOpen: PropTypes.func,
  hasNext: PropTypes.bool,
  hasPrev: PropTypes.bool,
  onNext: PropTypes.func,
  onPrev: PropTypes.func,
  onShowAnswers: PropTypes.func,
  onShowNextAnswer: PropTypes.func,
  onHideLastAnswer: PropTypes.func,
  enableShowAnswers: PropTypes.bool,
  enableCreateAssignment: PropTypes.bool,
  currentTab: PropTypes.number,
}

export default ReaderAppbar
